import React, { useEffect, useState, useMemo } from "react";
import "../../Styles/invoiceTable.css";
import { toast } from "react-toastify";
import { BsSearch } from "react-icons/bs";
import html2pdf from "html2pdf.js";
import TablePagination from "@mui/material/TablePagination";
import ReLoginModal from "../../components/ReLoginModal";
import * as XLSX from "xlsx";
import { CircularProgress } from "@mui/material";
import api from "../../util/axios";

type Requisition = {
  customer: { businessName: string };
  itemName: string;
  unitPrice: number;
  quantityToLoad: number;
  quantityLoaded: number;
  
};

type LoadingSlip = {
  truckNumber: string;
  loadingNumber: string;
  transporter: string;
  requisitions: Requisition[];
};

type Invoice = {
  _id: string;
  invoiceNumber: string;
  loadingSlip: LoadingSlip | null;
  updatedAt: string;
  createdAt: string;
  meterTicket?: string;
  dailyAllocation: { destination: string };
};

const InvoiceReport = () => {
  const [getRequisition, setGetRequisition] = useState<Invoice[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [page, setPage] = useState(0);
  const [reLogin, setReLogin] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [productQuery, setProductQuery] = useState("");
  const [businessQuery, setBusinessQuery] = useState("");
  const [truckQuery, setTruckQuery] = useState("");
  const [searchField, setSearchField] = useState("businessName");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const token = window.localStorage.getItem("token");

  useEffect(() => {
    queryRequisition();
  }, [fromDate, toDate]);

  const queryRequisition = () => {
    setIsLoading(true);
    setError(null);
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    const queryParams =
      fromDate || toDate
        ? `?startDate=${fromDate || ""}&endDate=${toDate || ""}`
        : "";

    api
      .get(`/invoice${queryParams}`, {
        headers,
      })
      .then((res) => {
        console.log("Invoice Report Data:", res.data.data.invoice);
        setGetRequisition(res.data.data.invoice);
      })
      .catch((error) => {
        if (
          error.response?.data?.message === "jwt expired" ||
          error.response?.data?.message === "jwt malformed"
        ) {
           
          setReLogin(true);
        } else {
          setError("Failed to fetch invoices. Please try again later.");
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const filteredRequisitions = useMemo(() => {
    return getRequisition.filter((invoice) => {
      const reqDate = new Date(invoice.createdAt);
      const startDate = fromDate ? new Date(fromDate) : null;
      const endDate = toDate ? new Date(toDate) : null;

      if (startDate && reqDate < startDate) return false;
      if (endDate && reqDate > endDate) return false;

      if (searchField === "combined") {
        if (!businessQuery && !productQuery && !truckQuery) return true;
      } else if (!searchQuery) {
        return true;
      }

      const requisitions = invoice.loadingSlip?.requisitions || [];

      const businessNameMatch = requisitions.some((req) =>
        req.customer?.businessName
          ?.toLowerCase()
          .includes(
            searchField === "combined"
              ? businessQuery.toLowerCase()
              : searchQuery.toLowerCase()
          )
      );
      const productNameMatch = requisitions.some((req) =>
        req.itemName
          ?.toLowerCase()
          .includes(
            searchField === "combined"
              ? productQuery.toLowerCase()
              : searchQuery.toLowerCase()
          )
      );
      const truckNumberMatch = invoice.loadingSlip?.truckNumber
        ?.toLowerCase()
        .includes(
          searchField === "combined"
            ? truckQuery.toLowerCase()
            : searchQuery.toLowerCase()
        );

      switch (searchField) {
        case "businessName":
          return businessNameMatch;
        case "productName":
          return productNameMatch;
        case "truckNumber":
          return truckNumberMatch;
        case "combined":
          return businessNameMatch && productNameMatch && truckNumberMatch;
        default:
          return businessNameMatch;
      }
    });
  }, [
    getRequisition,
    fromDate,
    toDate,
    searchQuery,
    productQuery,
    businessQuery,
    truckQuery,
    searchField,
  ]);

  const paginatedRequisitions = filteredRequisitions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const calculateTotalQuantityLoaded = (invoices: Invoice[]) => {
    return invoices.reduce((total, invoice) => {
      return (
        total +
        (invoice.loadingSlip?.requisitions?.reduce(
          (reqTotal, requisition) => reqTotal + (requisition.quantityLoaded || 0),
          0
        ) || 0)
      );
    }, 0);
  };

  const calculateCustomerQuantityLoaded = (invoices: Invoice[]) => {
    return invoices.reduce((acc, invoice) => {
      invoice.loadingSlip?.requisitions?.forEach((requisition) => {
        const customerName = requisition.customer?.businessName || "Unknown";
        acc[customerName] =
          (acc[customerName] || 0) + (requisition.quantityLoaded || 0);
      });
      return acc;
    }, {} as Record<string, number>);
  };

  const totalQuantityLoaded = calculateTotalQuantityLoaded(filteredRequisitions);
  const customerQuantities = calculateCustomerQuantityLoaded(filteredRequisitions);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const generatePDF = () => {
    const slipElement = document.querySelector(".invoiceTable");
    const dowBtnElement = document.querySelector(".dowBtn");

    if (slipElement) {
      if (dowBtnElement) (dowBtnElement as HTMLElement).style.display = "none";

      const pdfOptions = {
        margin: 2,
        filename: "Invoice-Report.pdf",
        image: { type: "png" },
        html2canvas: { scale: 2 },
        jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
      };
      html2pdf()
        .from(slipElement)
        .set(pdfOptions)
        .save()
        .finally(() => {
          if (dowBtnElement) (dowBtnElement as HTMLElement).style.display = "block";
        });
    }
  };

  const exportToExcelExternal = () => {
    if (!filteredRequisitions || filteredRequisitions.length === 0) {
      toast.warn("No data to export.");
      return;
    }

    const filteredData = filteredRequisitions.flatMap((invoice, index) => {
      const requisitions = invoice.loadingSlip?.requisitions || [];
      return requisitions.map((req, reqIdx) => ({
        "S/N": index + 1,
        "Date": new Date(invoice.updatedAt).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "2-digit",
        }),
        "Meter Ticket No.": invoice.meterTicket || "N/A",
        "Waybill No.": invoice.invoiceNumber || "N/A",
        "Quantity Loaded": req.quantityLoaded || 0,
        "Price": req.unitPrice || 0,
        "Truck No.": invoice.loadingSlip?.truckNumber || "N/A",
        "Transporter": invoice.loadingSlip?.transporter || "N/A",
        "Customer's Name": req.customer?.businessName || "N/A",
        "Retail Outlet/Consumer": invoice.dailyAllocation.destination,
        "Product Name": req.itemName || "N/A",
        
      }));
    });

    const ws = XLSX.utils.json_to_sheet(filteredData);
    ws['!cols'] = [
      { width: 5 },
      { width: 20 },
      { width: 20 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 20 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "InvoiceReports");
    XLSX.writeFile(wb, "Invoice-Report.xlsx");
  };

  const exportToExcel = () => {
    if (!filteredRequisitions || filteredRequisitions.length === 0) {
      toast.warn("No data to export.");
      return;
    }

    const filteredData = filteredRequisitions.flatMap((invoice, index) => {
      const requisitions = invoice.loadingSlip?.requisitions || [];
      return requisitions.map((req, reqIdx) => ({
        "S/N": index + 1,
        "Business Name": req.customer?.businessName || "N/A",
        "Product Name": req.itemName || "N/A",
        "Invoice No.": invoice.invoiceNumber || "N/A",
        "Loading No.": invoice.loadingSlip?.loadingNumber || "N/A",
        "Truck No.": invoice.loadingSlip?.truckNumber || "N/A",
        "Unit Price": req.unitPrice || 0,
        "Quantity to Load": req.quantityToLoad || 0,
        "Quantity Loaded": req.quantityLoaded || 0,
        "Date": new Date(invoice.updatedAt).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "2-digit",
        }),
      }));
    });

    const ws = XLSX.utils.json_to_sheet(filteredData);
    ws['!cols'] = [
      { width: 5 },
      { width: 25 },
      { width: 20 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 20 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "InvoiceReports");
    XLSX.writeFile(wb, "Invoice-Report.xlsx");
  };

  return (
    <div className="board">
      {reLogin ? <ReLoginModal /> : null}
      <div className="method">
        <div className="searchFieldSelect">
          <label htmlFor="filter">Search filter: </label>
          <select
            id="filter"
            value={searchField}
            onChange={(e) => setSearchField(e.target.value)}
          >
            <option value="businessName">Customer Name</option>
            <option value="productName">Product Name</option>
            <option value="truckNumber">Truck Number</option>
            <option value="combined">Mixed search</option>
          </select>
        </div>
        {searchField === "combined" ? (
          <div className="combinedSearch">
            <label htmlFor="businessQuery" className="sr-only">
              Customer Name
            </label>
            <input
              id="businessQuery"
              type="text"
              placeholder="Customer Name"
              value={businessQuery}
              onChange={(e) => setBusinessQuery(e.target.value)}
            />
            <label htmlFor="productQuery" className="sr-only">
              Product Name
            </label>
            <input
              id="productQuery"
              type="text"
              placeholder="Product Name"
              value={productQuery}
              onChange={(e) => setProductQuery(e.target.value)}
            />
            <label htmlFor="truckQuery" className="sr-only">
              Truck Number
            </label>
            <input
              id="truckQuery"
              type="text"
              placeholder="Truck Number"
              value={truckQuery}
              onChange={(e) => setTruckQuery(e.target.value)}
            />
          </div>
        ) : (
          <div className="apInput">
            <label htmlFor="searchQuery" className="sr-only">
              Search
            </label>
            <input
              id="searchQuery"
              type="text"
              value={searchQuery}
              placeholder="Search"
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <BsSearch className="apIcon" />
          </div>
        )}
        <div className="filterBy">
          Filter By:
          <label htmlFor="fromDate" className="sr-only">
            From Date
          </label>
          <input
            id="fromDate"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
          <label htmlFor="toDate" className="sr-only">
            To Date
          </label>
          <input
            id="toDate"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
      </div>
      <div className="invoiceTable">
        {isLoading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <CircularProgress />
            <h2>Please wait while we process the request...</h2>
          </div>
        ) : error ? (
          <div className="m-auto text-center px-4 py-4">
            <h1>Error</h1>
            <p>{error}</p>
          </div>
        ) : paginatedRequisitions.length > 0 ? (
          <div>
            <div className="table-wrapper">
              <table className="inTable">
                <thead>
                  <tr className="heTable">
                    <th>S/N</th>
                    <th>Customer Name</th>
                    <th>Product Name</th>
                    <th>Invoice No.</th>
                    <th>Loading No.</th>
                    <th>Truck No.</th>
                    <th>Unit Price</th>
                    <th>Ticket Number</th>
                    <th>Quantity Loaded</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRequisitions.map((invoice, index) => {
                    const requisitions = invoice.loadingSlip?.requisitions || [];
                    return requisitions.map((req, reqIdx) => (
                      <tr
                        key={`${invoice._id}-${reqIdx}`}
                        className="taData"
                      >
                        {reqIdx === 0 && (
                          <>
                            <td rowSpan={requisitions.length}>
                              {page * rowsPerPage + index + 1}
                            </td>
                            <td rowSpan={requisitions.length}>
                              {req.customer?.businessName || "N/A"}
                            </td>
                          </>
                        )}
                        <td>{req.itemName || "N/A"}</td>
                        {reqIdx === 0 && (
                          <>
                            <td rowSpan={requisitions.length}>
                              {invoice.invoiceNumber || "N/A"}
                            </td>
                            <td rowSpan={requisitions.length}>
                              {invoice.loadingSlip?.loadingNumber || "N/A"}
                            </td>
                            <td rowSpan={requisitions.length}>
                              {invoice.loadingSlip?.truckNumber || "N/A"}
                            </td>
                          </>
                        )}
                        <td>{req.unitPrice || 0}</td>
                        <td>{invoice.meterTicket || "N/A"}</td>
                        <td>{req.quantityLoaded || 0}</td>
                        {reqIdx === 0 && (
                          <td rowSpan={requisitions.length}>
                            {new Date(invoice.updatedAt).toLocaleDateString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "short",
                                day: "2-digit",
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )}
                          </td>
                        )}
                      </tr>
                    ));
                  })}
                </tbody>
              </table>
            </div>
            <div className="aggregate">
              {Object.entries(customerQuantities).map(([customer, quantity]) => (
                <div key={customer}>
                  <strong>{customer}: </strong>
                  {quantity}
                </div>
              ))}
              <div>
                <strong>Total Quantity Loaded: </strong>
                {totalQuantityLoaded}
              </div>
            </div>
          </div>
        ) : (
          <div className="m-auto text-center px-4 py-4">
            <h1>No data available.</h1>
            <p>Please check back later or adjust your filters.</p>
          </div>
        )}
      </div>

      <TablePagination
        rowsPerPageOptions={[15, 25, 50]}
        component="div"
        count={filteredRequisitions.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <div className="dowBtn">
        <button onClick={generatePDF}>Print Report</button>
        <button onClick={exportToExcel}>Export</button>
        <button onClick={exportToExcelExternal}>Export ext</button>
      </div>
    </div>
  );
};

export default InvoiceReport;